import { FileUpload, Upload } from 'graphql-upload';
import {
  ATSOrigin,
  ContractLegalStatus,
  SupplierInvoiceStatus,
  TemplateContract,
  IMission,
  IPricing,
  ISociety,
  INotification,
  SocietyFiles,
  ResolvedContact,
} from './types.shared';
import {
  InvoiceStatus,
  BusinessType,
  DealType,
  JobType,
  CRAStatus,
  LostReasonType,
  StatusType,
  Staff,
  TalentBoardJobFunnelPhase,
  TalentBoardEventOpenTalent,
  TalentBoardEventUpsertComment,
  TalentBoardEventInvitationCreate,
  TalentBoardEventInvitationDelete,
  TalentBoardEventTalentMarkedAsNotSeen,
  TalentBoardPersistedEventType,
  PurchaseOrderStatus,
} from './domain';
import { BackgroundType, ProfileNetwork } from './talent';

export type { FileUpload };

// In graphql data that can be undefined, is always returned as null
export type Maybe<T> = T | null;

// related to type Mision in schema.bloomers.graphql and ALL_MISSIONS gql query
export type AllMissionGQLItemResult = {
  recordId: string;
  label: Maybe<string>;
  beginMission: string;
  endMission: string;
  createdAt: string;
  bloomerFullName: Maybe<string[]>;
  clientSocietyName: string[];
  bloomerSocietyName: Maybe<string[]>;
  isActive: Maybe<boolean>;
  replacement: Maybe<boolean>;
  type: Maybe<BusinessType>;
  pricing: Maybe<IPricing[]>;
  jobOwner?: Maybe<Pick<GqlAccountManager, 'firstname' | 'lastname' | 'email'>>;
};

// see ALL_MISSIONS_WITH_EXTENSION gql query
export interface AllMissionWithExtensionGQLItemResult extends AllMissionGQLItemResult {
  extension: Maybe<Pick<ExtensionAssessmentGQL, 'assessment' | 'newEndDate'>>;
}

export type AllMissionGraphQLQueryParams =
  | {
      isActive: boolean;
      missionIds?: string[];
    }
  | undefined;

export type AllMissionGQLResult<T> = {
  count: number;
  results: T[];
};

export type UpdateMissionGQLInput = {
  recordId: string;
} & Partial<IMission>;

export type DeclareZeroActivityForMissionInput = {
  missionId: string;
  month: Date;
};

export type CreateActivityForMissionInput = {
  missionId: string;
  month: Date;
};

export type UpsertExtensionAssessmentGQLInput = {
  missionId: string;
  assessment: number;
  newEndDate: Date;
};

export type ExtensionAssessmentGQL = {
  missionId: string;
  assessment: number;
  updatedAt: Date;
  newEndDate: Date;
  updatedBy: string;
};

export type CreateMissionAmendmentGQLInput = {
  newEndMission: Maybe<string>;
  recordId: string;
  useTemplateForSST: boolean;
  useTemplateForPDS: boolean;
  newPurchasePrice: Maybe<number>;
  newSalePrice: Maybe<number>;
  newPricingDate: Maybe<string>;
  firstClause: string;
  secondClauseForSST: string;
  secondClauseForPDS: string;
};

export type ShortenMissionGQLInput = {
  endMission: string;
  recordId: string;
};

export type GetExtensionAssessmentGQLResult = {
  getExtensionAssessment: Maybe<ExtensionAssessmentGQL>;
};

/** Destructured CRA signature (parsed server-side from the embed id). */
export type GqlActivitySignature = {
  signedAt: Maybe<Date>;
  ip: Maybe<string>;
  signerName: Maybe<string>;
};

export type CRADashboardItemGQL = {
  missionId: string;
  missionLabel: string;
  bloomerFullName: string;
  clientSocietyName: string;
  contactOperationsName: string;
  activityId: Maybe<string>;
  activityMonth: Maybe<Date>;
  status: CRAStatus;
  craURL: Maybe<string>;
  /** Numéro de BDC retenu à la facturation, dénormalisé sur `activity.orderNumber` (TEC-3291). */
  orderNumber?: Maybe<string>;
  invoiceURL: Maybe<string>;
  /** URL S3 de la facture additionnelle Bloomer (traitement manuel, optionnelle). */
  additionalInvoiceURL?: Maybe<string>;
  signingDocumentId: Maybe<string>;
  /** Nb de signatures CRA (0 = aucune, 1 = Bloomer signé, 2 = Bloomer + client). */
  signedCount?: Maybe<number>;
  /** Signature Bloomer parsée (présente ⇒ le Bloomer a signé). */
  bloomerSignatureInfo?: Maybe<GqlActivitySignature>;
  /** Signature client parsée (présente ⇒ le client a signé). */
  clientSignatureInfo?: Maybe<GqlActivitySignature>;
  bloomerPayDateDays: Maybe<number>;
  dateUploadInvoice: Maybe<Date>;
  bloomerInvoiceBilled: boolean;
  bloomerInvoiceStatus: InvoiceStatus;
  bloomerInvoiceLastError: Maybe<string>;
  supplierInvoiceStatus: Maybe<SupplierInvoiceStatus>;
  externalSupplierInvoiceId: Maybe<string>;
  externalCustomerInvoiceId?: Maybe<string>;
  /** Pennylane external id of the markup (HNO) client invoice, when generated. */
  externalCustomerInvoiceMarkupId?: Maybe<string>;
  /** Pennylane deep link to the client invoice — resolved API-side from the external id. */
  clientInvoicePennylaneUrl?: Maybe<string>;
  /** Pennylane deep link to the markup (HNO) client invoice — resolved API-side. */
  clientInvoiceMarkupPennylaneUrl?: Maybe<string>;
  /** Pennylane deep link to the Bloomer/supplier invoice — resolved API-side. */
  bloomerInvoicePennylaneUrl?: Maybe<string>;
  bloomerSocietyName: Maybe<string>;
  bloomerSocietyPortage: Maybe<boolean>;
  clientInvoiceStatus: InvoiceStatus;
  clientInvoiceLastError: Maybe<string>;
  /** `true` once the client invoice is billed (Airtable `billed`). */
  clientInvoiceBilled?: Maybe<boolean>;
  bloomerScore: Maybe<number>;
  bloomerComment: Maybe<string>;
  bloomerScoreDate: Maybe<Date>;
  clientScore: Maybe<number>;
  clientComment: Maybe<string>;
  clientScoreDate: Maybe<Date>;
  nbDaysWorked: Maybe<number>;
  nbDaysMarkupWorked: Maybe<number>;
  nbDaysMarkup2Worked: Maybe<number>;
  nbDaysAstreinteWorked: Maybe<number>;
  nbDaysDetails: Maybe<string>;
  hoursPerDays: Maybe<number>;
  /** Prix de vente HT / jour du tarif valide sur le mois du CRA (null si inconnu). */
  salePricePerDayWithoutTaxes: Maybe<number>;
  /** Prix d'achat HT / jour (montant reversé au Bloomer) du tarif valide sur le mois (null si inconnu). */
  purchasePricePerDayWithoutTaxes: Maybe<number>;
  /** Coefficient d'astreinte du tarif valide (défaut métier 2) — alimente le calcul des montants. */
  astreinteCoef: Maybe<number>;
  /** Account manager (job owner) de la mission — résolu paresseusement côté API. */
  jobOwner?: Maybe<Pick<GqlAccountManager, 'firstname' | 'lastname' | 'email'>>;
  /** Mission externalId (pipefyId ou hiresweetId) — interne, sert au résolveur `jobOwner`. */
  missionExternalId?: Maybe<string>;
  /** Origine ATS de la mission — interne, lève l'ambiguïté sur `missionExternalId`. */
  missionOrigin?: Maybe<ATSOrigin>;
  /** Au moins une modification manuelle des jours enregistrée pour ce CRA. */
  daysManuallyModified: boolean;
};

export type CraDaysManualEditEntry = {
  editedAt: Date;
  editedBy: string;
  reason: string;
  beforeNormalWorkedDays: number;
  beforeSaturdaysWorkedDays: number;
  beforeSundayAndHolidaysWorkedDays: number;
  beforeAstreinteWorkedDays: number;
  afterNormalWorkedDays: number;
  afterSaturdaysWorkedDays: number;
  afterSundayAndHolidaysWorkedDays: number;
  afterAstreinteWorkedDays: number;
};

export type EditCraDaysManualInput = {
  craAirtableId: string;
  normalWorkedDays: number;
  saturdaysWorkedDays: number;
  sundayAndHolidaysWorkedDays: number;
  astreinteWorkedDays: number;
  reason?: Maybe<string>;
};

export type FileUploadedGQL = {
  path: string;
  url: string;
};

export type FileUploadGQL = Upload;

export type UploadGQLInput = FileUploadGQL | FileUploadedGQL;

export interface SocietyGQLInput extends Omit<ISociety, SocietyFiles> {
  KBIS?: UploadGQLInput;
  URSSAFVigilanceCertificate?: UploadGQLInput;
  RCPVigilanceCertificate?: UploadGQLInput;
}

export interface SocietyGQLQueryInput {
  recordIds?: string[];
  isClient?: boolean;
}

export type LegalContractGQL = {
  id: number;
  recordId: string;
  nbSigner: number;
  signerDocId1: Maybe<string>;
  createdAt: Date;
  createdDate: string;
  signedCount: number;
  signedDate: Maybe<string>;
  smartFields?: Maybe<string>;
  status: ContractLegalStatus;
  provider: Maybe<string>;
  worflowExternalId?: string;
  contractTemplate: Maybe<string>;
  type: TemplateContract;
  step: string;
  emptyFileUrl: Maybe<string>;
  signedFileUrl: Maybe<string>;
  externalDocumentId: Maybe<string>;
  refusedReason: Maybe<string>;
  signLink: Maybe<string>;
};

export enum MissionStatusFilter {
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  INCOMING = 'INCOMING',
}

export type MissionDataForGetLegalContractsResult = {
  id: string;
  label: string;
  bloomerId: string;
  bloomerFullName: string;
  clientSocietyId: string;
  clientSocietyName: string;
  bloomerSocietyName: Maybe<string>;
  accountManager?: Maybe<Pick<GqlAccountManager, 'firstname' | 'lastname'>>;
  /** Date de début de mission (ISO). Référentiel du statut dérivé « en retard » du contrat principal. */
  beginMission?: Maybe<string>;
  contractType?: Maybe<string>;
  resolvedClientPdsContact?: Maybe<ResolvedContact>;
  resolvedSupplierSstContact?: Maybe<ResolvedContact>;
};

export type MissionDataForGetLegalContracts = MissionDataForGetLegalContractsResult & {
  externalId?: string;
  /** Origine ATS de la mission — interne, sert au résolveur `accountManager`. */
  origin?: ATSOrigin;
};

export type ContractsByMissionGQL = {
  mission: MissionDataForGetLegalContractsResult;
  contracts: LegalContractGQL[];
};

export type ContractSegmentSummaryGQL = {
  toSign: number;
  late: number;
  missionsConcerned: number;
};

export type ContractSegmentsSummaryGQL = {
  contractSst: ContractSegmentSummaryGQL;
  contractPds: ContractSegmentSummaryGQL;
  amendmentSst: ContractSegmentSummaryGQL;
  amendmentPds: ContractSegmentSummaryGQL;
};

/** Synthèse globale toutes missions confondues (bandeau d'alerte). */
export type GlobalContractSummaryGQL = {
  toSign: number;
  late: number;
  missionsConcerned: number;
  segments: ContractSegmentsSummaryGQL;
};

/**
 * Filtrable document status. `pending` = generation error (≠ awaiting signature = `toSign`).
 * `late` is derived (unsigned principal + mission started), not a stored value.
 */
export enum DocumentStatusFilter {
  toSign = 'toSign',
  signed = 'signed',
  pending = 'pending',
  refused = 'refused',
  cancelled = 'cancelled',
  late = 'late',
}

export enum LineTypeFilter {
  all = 'all',
  principal = 'principal',
  avenant = 'avenant',
}

export enum ContractVoletFilter {
  SST = 'SST',
  PDS = 'PDS',
}

export type ContractsFilterInput = {
  documentStatus?: Maybe<DocumentStatusFilter[]>;
  lineType?: Maybe<LineTypeFilter>;
  accountManagerEmail?: Maybe<string[]>;
  volet?: Maybe<ContractVoletFilter[]>;
};

export type ContractsPaginationInput = {
  limit?: Maybe<number>;
  offset?: Maybe<number>;
};

export type ContractsPageInfoGQL = {
  total: number;
  limit: Maybe<number>;
  offset: number;
  hasNextPage: boolean;
};

export type AvailableAccountManagerGQL = {
  email: string;
  name: string;
};

export type LegalContractsWithSummaryGQL = {
  missions: ContractsByMissionGQL[];
  summary: GlobalContractSummaryGQL;
  pageInfo: ContractsPageInfoGQL;
  /** AM options for the filter dropdown; independent of the current AM selection. */
  availableAccountManagers: AvailableAccountManagerGQL[];
};

export type GetLegalContractsByMissionGQLResult = {
  getLegalContractsByMission: ContractsByMissionGQL;
};

export type GetLegalContractsWithSummaryGQLResult = {
  getLegalContractsWithSummary: LegalContractsWithSummaryGQL;
};

export type GetLegalContractsGQLParams = {
  status: MissionStatusFilter;
  filter?: Maybe<ContractsFilterInput>;
  search?: Maybe<string>;
  pagination?: Maybe<ContractsPaginationInput>;
};

export type UploadAmendmentGQLInput = {
  file: Upload;
  legalContractRecordId: string;
  missionRecordId: string;
  signed: boolean;
  createInvitation: boolean;
  signedDate?: string;
};

export type UploadAmendmentGQLResult = {
  uploadAmendment: {
    recordId: string;
    type: TemplateContract;
    step: string;
    status: ContractLegalStatus;
    nbSigner: number;
    signedCount: number;
    emptyFileUrl: Maybe<string>;
    signedFileUrl: Maybe<string>;
    signedDate: Maybe<Date>;
    externalDocumentId: string;
  };
};

export type PurchaseOrdersFilterGQLInput = {
  status?: PurchaseOrderStatus;
  missionRecordId?: string;
  clientSocietyRecordId?: string;
};

export type CreatePurchaseOrderGQLInput = {
  orderNumber: string;
  clientSocietyRecordId: string;
  missionsRecordId?: string[];
  startDate?: Date;
  endDate?: Date;
  amountHT?: number;
  useForNonWorkingHours?: boolean;
  invoiceComment?: string;
};

export type UpdatePurchaseOrderGQLInput = {
  recordId: string;
  orderNumber?: string;
  clientSocietyRecordId?: string;
  missionsRecordId?: string[];
  startDate?: Date;
  endDate?: Date;
  amountHT?: number;
  invoicedAmountHT?: number;
  useForNonWorkingHours?: boolean;
  invoiceComment?: string;
  status?: PurchaseOrderStatus;
};

export type UploadPurchaseOrderFileGQLInput = {
  recordId: string;
  file: Upload;
};

export type PurchaseOrderMissionTier = 'ok' | 'expired' | 'monthPlus1Missing' | 'monthPlus2Missing';

export type PurchaseOrderCoverageBreakdown = {
  count: number;
  missionRecordIds: string[];
};

export type PurchaseOrderCoverageCounts = {
  received: PurchaseOrderCoverageBreakdown;
  waiting: PurchaseOrderCoverageBreakdown;
  missing: PurchaseOrderCoverageBreakdown;
};

export type PurchaseOrderMissionTierBreakdown = {
  count: number;
  missionRecordIds: string[];
};

export type PurchaseOrderMissionTierCounts = {
  expired: PurchaseOrderMissionTierBreakdown;
  monthPlus1Missing: PurchaseOrderMissionTierBreakdown;
  monthPlus2Missing: PurchaseOrderMissionTierBreakdown;
};

export type PurchaseOrderDashboardKpis = {
  referenceDate: Date;
  atDate: PurchaseOrderCoverageCounts;
  monthMinus1: PurchaseOrderCoverageCounts;
  currentMonth: PurchaseOrderCoverageCounts;
  monthPlus1: PurchaseOrderCoverageCounts;
  monthPlus2: PurchaseOrderCoverageCounts;
  monthPlus3: PurchaseOrderCoverageCounts;
  tierCounts: PurchaseOrderMissionTierCounts;
};

export type PurchaseOrderMissionCoverage = {
  missionRecordId: string;
  missionLabel?: Maybe<string>;
  clientSocietyName?: Maybe<string>;
  tier: PurchaseOrderMissionTier;
  hasReceivedPurchaseOrder: boolean;
  coversMonthMinus1: boolean;
  coversCurrentMonth: boolean;
  coversMonthPlus1: boolean;
  coversMonthPlus2: boolean;
  coversMonthPlus3: boolean;
  /**
   * Décalages de mois (relatifs au mois de la date de référence) où la mission est
   * active sans BDC couvrant — un mois d'inactivité n'y figure pas.
   */
  monthsWithoutPurchaseOrder: number[];
};

export type MissionPricingHistoryItem = Pick<
  IPricing,
  'recordId' | 'startDate' | 'salePricePerDayWithoutTaxes' | 'purchasePricePerDayWithoutTaxes' | 'astreinteCoef'
>;

export type GetMissionPricingsParams = {
  missionRecordId: string;
};

export type GetMissionPricingsResponse = {
  missionPricings: MissionPricingHistoryItem[];
};

export type GetOneFullMissionParams = {
  recordId: string;
};

export type GetLegalContactMissionParams = {
  recordId: string;
};

export type GetLegalContactMissionResponse = {
  missions: Array<{
    recordId: string;
    resolvedClientPdsContact: Maybe<ResolvedContact>;
    resolvedSupplierSstContact: Maybe<ResolvedContact>;
  }>;
};

export type SyncCandidatJobInput = {
  jobId: string;
  talentId: string;
  author: string;
};

export type QueryResult<T> = { count: number; results: T[] };
export type IMissionQueryResult = QueryResult<Partial<IMission>>;

export type GqlSociety = {
  name: string;
  siret?: Maybe<string>;
};

export type GqlAccountManager = {
  firstname: string;
  lastname: string;
  email: string;
  role: Staff;
  avatarUrl: Maybe<string>;
  phoneNumber: Maybe<string>;
  externalId: Maybe<string>;
};

export type GetOneFullMissionResponse = {
  missions: Array<IMission & { jobOwner?: GqlAccountManager | null }>;
};

// requested fields defined by JOB_FUNNEL_BASE_FIELDS
export interface GqlJobFunnel {
  id: string;
  name: string;
  jobType: JobType;
  dealType: Maybe<DealType>;
  jobTitle: string;
  society: Pick<GqlSociety, 'name'>;
  recruiter?: Maybe<Pick<GqlAccountManager, 'email' | 'firstname' | 'lastname'>>;
  jobOwner?: Maybe<Pick<GqlAccountManager, 'email' | 'firstname' | 'lastname'>>;
  team?: Maybe<string>;
  context?: Maybe<string>;
  location?: Maybe<string>;
  duration?: Maybe<number>;
  onSiteDaysPerWeek?: Maybe<number>;
  remoteDaysPerWeek?: Maybe<number>;
  expectedStartDate?: Maybe<string>;
  details?: Maybe<string>;
  scoreCard1?: Maybe<string>;
  scoreCard2?: Maybe<string>;
  scoreCard3?: Maybe<string>;
  scoreCard4?: Maybe<string>;
  externalId?: Maybe<string>;
  minYearsXP?: Maybe<number>;
  maxClientBudget?: Maybe<number>;
  clientBudgetDetails?: Maybe<string>;
  maxTalentBudget?: Maybe<number>;
  outOfBudgetProfil?: Maybe<string>;
  budgetDecisionMaker?: Maybe<string>;
  isInternalValidation?: Maybe<boolean>;
  clientResearchTrigger?: Maybe<string>;
  interviewProcess?: Maybe<string>;
  finalValidationResponsable?: Maybe<string>;
  minTotalProfilesToInclude?: Maybe<string>;
  searchStatus?: Maybe<string>;
  competitors?: Maybe<string>;
  isExclusivity?: Maybe<boolean>;
  cvDebriefDate?: Maybe<string>;
  isResponsiveClient?: Maybe<boolean>;
  isReliableClient?: Maybe<boolean>;
  isDirectLinkWithDecisionMaker?: Maybe<boolean>;
  priority?: Maybe<string>;
  isBloomerReplacement?: Maybe<boolean>;
  isQuickContract?: Maybe<boolean>;
  status?: Maybe<StatusType>;
  lostReason?: Maybe<LostReasonType>;
  otherLostReason?: Maybe<string>;
  bloomerSource?: Maybe<string>;
  commercialMonth?: Maybe<Date>;
  passeur?: Maybe<string>;
  buteur?: Maybe<string>;
  capitaine?: Maybe<string>;
  defenseur?: Maybe<string>;
  requiredLanguages?: Maybe<string>;
  interlocuteur1Email?: Maybe<string>;
  interlocuteur2Email?: Maybe<string>;
  createdAt?: Maybe<Date>;
  hubspotDealId?: Maybe<string>;
  airtableId?: Maybe<string>;
  pipefyId?: Maybe<string>;
  hiresweetId?: Maybe<string>;
  winDate?: Maybe<Date>;
}

export interface GqlDealJobFunnel extends GqlJobFunnel {
  leadOrigin?: Maybe<string>;
  leadCustomerStatus?: Maybe<string>;
  leadCanal?: Maybe<string>;
  leadCanalDetail?: Maybe<string>;
}

export type GqlTalent = {
  id: string;
  firstname: string;
  lastname: string;
  email: Maybe<string>;
  jobTitle: string;
};

export type GqlTalentForTalentBoard = GqlTalent & {
  image: Maybe<string>;
  availabilityDate: Maybe<Date>;
  location: Maybe<{
    id: string;
    city: string;
  }>;
  tjm: Maybe<string>;
  package?: Maybe<string>;
  profiles: GqlProfile[];
  cv: Maybe<string>;
  jobs: GqlJobTalent[];
  educations: GqlEducation[];
  languages: GqlLanguage[];
  phase: TalentBoardJobFunnelPhase;
  phaseNotes: GqlTalentPhaseNote[];
  topProfile: boolean;
  cardTamComment: Maybe<string>;
  generalTamEvaluation: Maybe<string>;
  comments: GqlTalentBoardComment[];
  addedInProject: Maybe<Date>;
  markedAsSeen: boolean;
  dateMarkedAsSeen: Maybe<Date>;
  becameBloomer: boolean;
  archivedAt: Maybe<Date>;
};

export type GqlTalentJobFunnel = {
  talentId: string;
  jobId: string;
  topProfile: boolean;
  addedInProject: Maybe<Date>;
  markedAsSeen: boolean;
  dateMarkedAsSeen: Maybe<Date>;
};

export type GqlProfile = {
  id: Maybe<string>;
  network: Maybe<ProfileNetwork | string>;
  url: Maybe<string>;
  username: Maybe<string>;
  talentId: Maybe<string>;
};

export type GqlTalentPhaseNote = {
  id: string;
  phaseId: string;
  date: Date;
  answers: Record<string, string>;
  jobId: string;
  talentId: string;
  source: string;
  externalId: string;
};

export type GqlTalentBoardComment = {
  id: string;
  jobId: string;
  talentId: string;
  comment: string;
  author: string;
  createdAt: Date;
  updatedAt: Date;
};

export type GqlJobTalent = {
  id: Maybe<string>;
  jobTitle: { id: string; label: string; iso: boolean | null }[];
  period: Maybe<{
    id: string;
    startDate: Date;
    endDate: Maybe<Date>;
  }>;
  current: Maybe<boolean>;
  label: Maybe<string>;
  description: Maybe<string>;
  name: Maybe<string>;
  skills: Maybe<GqlSkill[]>;
};

export type GqlEducation = {
  id: Maybe<string>;
  name: string;
  label: Maybe<string>;
  description: Maybe<string>;
  current: Maybe<boolean>;
  url: Maybe<string>;
  type: Maybe<BackgroundType>;
  period: Maybe<{
    id: string;
    startDate: Date;
    endDate: Maybe<Date>;
  }>;
};

export type GqlLanguage = {
  id: Maybe<string>;
  label: string;
  level: Maybe<number>;
};

export type GqlSkill = {
  id: string;
  label: string;
  iso: boolean;
  alternateLabels: string[];
};

export type GqlJobFunnelWithBloomer = {
  job: GqlDealJobFunnel;
  talents: Maybe<GqlTalent[]>;
  bloomer: Maybe<GqlTalent>;
};

export type GetCurrentProjectsFilterInput = {
  role: 'tam' | 'client' | 'system' | 'anonymous';
  email: string;
};

export type GqlSynchroTalentFromATSResult = {
  success: boolean;
  errors: string[];
};

export type GqlSynchroTalentFromATSOutput = {
  talent: GqlSynchroTalentFromATSResult;
  resume: GqlSynchroTalentFromATSResult;
  notes: GqlSynchroTalentFromATSResult;
};

export type GqlTalentBoardInvitation = {
  id: string;
  jobId: string;
  email: string;
  createdAt: Date;
  createdBy: string;
  resentAt: Maybe<Date>;
};

export type GqlTalentBoardEventKpis = {
  talentBoardOpened: number;
  talentViewed: number;
  commentUpserted: number;
};

export type GqlTalentBoardEvent =
  | TalentBoardEventOpenTalent
  | TalentBoardEventUpsertComment
  | TalentBoardEventInvitationCreate
  | TalentBoardEventInvitationDelete
  | TalentBoardEventTalentMarkedAsNotSeen
  | ({
      id: string;
      jobId: string;
      date: Date;
      authorEmail: string;
    } & { type: TalentBoardPersistedEventType.TalentboardOpenJob; data: null });

// Ces types représentent ce qui est renvoyé par les requêtes Apollo et qui est vu côté front, donc il est fortement couplé à la techno

export type ApolloGqlAccountManager = GqlAccountManager & {
  __typename: 'GqlAccountManager';
};

export type ApolloGqlJobFunnel = Required<GqlJobFunnel> & {
  __typename: 'GqlJobFunnel';
} & {
  society: GqlJobFunnel['society'] & {
    __typename: 'GqlSociety';
  };
  recruiter: GqlJobFunnel['recruiter'] & {
    __typename: 'GqlAccountManager';
  };
  jobOwner: GqlJobFunnel['jobOwner'] & {
    __typename: 'GqlAccountManager';
  };
};

export type ApolloGqlTalentForTalentBoard = Required<GqlTalentForTalentBoard> & {
  __typename: 'GqlTalentForTalentBoard';
  profiles: (Required<GqlProfile> & {
    __typename: 'GqlProfile';
  })[];
  jobs: (Required<GqlJobTalent> & {
    __typename: 'GqlJobTalent';
  })[];
  educations: (Required<GqlEducation> & {
    __typename: 'GqlEducation';
  })[];
  languages: (Required<GqlLanguage> & {
    __typename: 'GqlLanguage';
  })[];
  phaseNotes: (Required<GqlTalentPhaseNote> & {
    __typename: 'GqlTalentPhaseNote';
  })[];
};

export type ApolloGqlJobFunnelWithTalents = {
  __typename: 'GqlJobFunnelWithTalents';
  job: Required<GqlJobFunnelWithBloomer['job']> & {
    __typename: 'GqlDealJobFunnel';
  } & {
    society: GqlJobFunnel['society'] & {
      __typename: 'GqlSociety';
    };
    recruiter: GqlJobFunnel['recruiter'] & {
      __typename: 'GqlAccountManager';
    };
    jobOwner: GqlJobFunnel['jobOwner'] & {
      __typename: 'GqlAccountManager';
    };
  };
  talents: (Required<GqlTalent> & {
    __typename: 'GqlTalent';
  })[];
  bloomer: Required<GqlTalent> & {
    __typename: 'GqlTalent';
  };
};

export type ApolloGqlTalentBoardStats = {
  contacted: number;
  __typename: 'GqlTalentBoardStats';
};

export type NotificationHistoryGQL = {
  id?: string;
  processedOn?: number;
  error?: string | null;
  progress?: number;
  data: INotification;
};
